import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {AsteriskModule} from 'src/app/directives/mark-asterisk/mark-asterisk.module';
import {UiKitsModule} from 'src/app/shared/ui-kits/ui-kits.module';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {WorkingPeriodService} from 'src/app/services/working-period/working-period.service';
import {Utils} from 'src/app/shared/utils/utils';
import {
  IIncreasePeriodDropdowns,
  workingPeriodStatus,
} from 'src/app/interfaces/termination.interface';

@UntilDestroy()
@Component({
  selector: 'app-increase-employment-position-form',
  standalone: true,
  imports: [
    CommonModule,
    UiKitsModule,
    ReactiveFormsModule,
    AsteriskModule,
  ],
  templateUrl: './increase-employment-position-form.component.html',
  styleUrls: ['./increase-employment-position-form.component.scss'],
})
export class IncreaseEmploymentPositionFormComponent
  implements OnInit, OnDestroy
{
  @Input() increasePeriodDropdowns!: IIncreasePeriodDropdowns;
  @Input() isPMAllowToChoose!: boolean;

  public workingPeriodForm!: FormGroup;
  public isFieldInvalid = Utils.isFieldInvalid;

  constructor(protected workingPeriod: WorkingPeriodService) {}

  ngOnInit(): void {
    this.workingPeriodForm = this.workingPeriod.getWorkingPeriod;
    this.setEmployeeFormControls();
    this.setFormValidation();
    if (this.isFormer) this.removeValidationForEmployeeForm();
    this.subscribeToNewStatusChanges();
  }

  subscribeToNewStatusChanges() {
    this.workingPeriod
      .formControl('newStatus')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.setFormValidation();
      });
  }

  setFormValidation() {
    if (this.workingPeriod.isNewStatusContractor) {
      this.workingPeriod.rate!.addValidators([
        Validators.required,
        Validators.min(0.1),
      ] as ValidatorFn[]);
      this.workingPeriod.stavka!.setValidators(
        Validators.min(0) as ValidatorFn
      );
    } else if (this.workingPeriod.isNewStatusEmployee) {
      this.workingPeriod.stavka!.addValidators([
        Validators.required,
        Validators.min(0.1),
      ] as ValidatorFn[]);
      this.workingPeriod.rate!.addValidators([
        Validators.min(0),
        Validators.required,
      ] as ValidatorFn[]);
    }
  }

  setEmployeeFormControls() {
    this.workingPeriodForm.addControl(
      'startDate',
      new FormControl<string | null>(null, [Validators.required])
    );
    this.workingPeriodForm.addControl(
      'rate',
      new FormControl<number | null>(0, [Validators.max(999)])
    );
    this.workingPeriodForm.addControl(
      'stavka',
      new FormControl<number | null>(null, [Validators.max(999999)])
    );
    this.workingPeriodForm.addControl(
      'projectName',
      new FormControl<string | null>(null, [Validators.required])
    );
    this.workingPeriodForm.addControl(
      'newPM',
      new FormControl<IDropdownObject | null>(
        null,
        this.isPMAllowToChoose ? [Validators.required] : null
      )
    );
    this.workingPeriodForm.addControl(
      'newLineManager',
      new FormControl<IDropdownObject | null>(
        null,
        this.isPMAllowToChoose ? null : [Validators.required]
      )
    );
  }

  removeEmployeeFormControls() {
    this.workingPeriodForm.removeControl('startDate');
    this.workingPeriodForm.removeControl('rate');
    this.workingPeriodForm.removeControl('stavka');
    this.workingPeriodForm.removeControl('projectName');
    this.workingPeriodForm.removeControl('newPM');
    this.workingPeriodForm.removeControl('newLineManager');
  }

  removeValidationForEmployeeForm() {
    this.workingPeriod
      .formControl('corporateAccount')
      .removeValidators(Validators.required);
    this.workingPeriod
      .formControl('equipment')
      .removeValidators(Validators.required);
    this.workingPeriod
      .formControl('location')
      .removeValidators(Validators.required);
  }

  setValidationForEmployeeForm() {
    this.workingPeriod.setValidators('corporateAccount', Validators.required);
    this.workingPeriod.setValidators('equipment', Validators.required);
    this.workingPeriod.setValidators('location', Validators.required);
  }

  setFormControlValue(controlName: string, value: any) {
    this.workingPeriod.formControl(controlName)?.setValue(value);
  }

  get rateClass(): string {
    return !this.workingPeriod.isNewStatusContractor ? 'col-sm-4' : 'col-sm-5';
  }

  get rateInputClass(): string {
    return !this.workingPeriod.isNewStatusContractor ? 'col-sm-6' : 'col-sm-7';
  }

  get isFormer(): boolean {
    return this.workingPeriod.getCurrentStatus === workingPeriodStatus.Former;
  }

  get pmOptionsList(): IDropdownObject[] {
    return this.increasePeriodDropdowns.pmOptionsList;
  }

  get lmOptionsList(): IDropdownObject[] {
    return this.increasePeriodDropdowns.lmOptionsList;
  }

  get equipmentOptionsList(): IDropdownObject[] {
    return this.increasePeriodDropdowns.equipmentOptionsList;
  }

  get corporateAccountOptionsList(): IDropdownObject[] {
    return this.increasePeriodDropdowns.corporateAccountOptionsList;
  }

  ngOnDestroy(): void {
    this.removeEmployeeFormControls();
    this.setValidationForEmployeeForm();
  }
}
